'use client';

import 'swiper/css';

import { motion } from 'framer-motion';
import {
  ChartLine,
  ChevronLeft,
  ChevronRight,
  CloudCheck,
  HandPlatter,
  QrCode,
  ShoppingBag,
  Zap,
} from 'lucide-react';
import Image, { type StaticImageData } from 'next/image';
import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Autoplay, Navigation, Thumbs } from 'swiper/modules';
import type { SwiperClass } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
  Feature1Image,
  Feature2Image,
  Feature3Image,
  Feature4Image,
  Feature5Image,
  Feature6Image,
  Feature7Image,
  Feature8Image,
} from '@/assets';

import IconButton from '../common/IconButton';

interface HighlightProps {
  icon: ReactElement;
  image: StaticImageData;
  title: string;
  subtitle: string;
  desc: string;
  features: {
    title: string;
    desc: string;
  }[];
  bgClassName: string;
  textClassName: string;
  borderClassName: string;
  bulletClassName: string;
}

const highlights: HighlightProps[] = [
  {
    icon: <CloudCheck />,
    image: Feature8Image,
    title: 'Đồng bộ\ntức thời',
    subtitle: 'Đồng bộ tức thời - Quản lý dễ dàng, thông tin luôn chính xác',
    desc: 'Tính năng đồng bộ dữ liệu theo thời gian thực đảm bảo thông tin kinh doanh luôn được cập nhật chính xác, giúp tối ưu hóa quy trình vận hành và hỗ trợ điều hành chuỗi cửa hàng một cách hiệu quả và liền mạch.',
    features: [
      {
        title: 'Cập nhật liên tục:',
        desc: 'Dữ liệu được làm mới tức thì, đảm bảo thông tin luôn chính xác và kịp thời.',
      },
      {
        title: 'Đồng bộ trên mọi thiết bị:',
        desc: 'Quản lý cửa hàng dễ dàng từ điện thoại, máy tính bảng đến laptop, không bỏ lỡ bất kỳ thông tin quan trọng nào.',
      },
      {
        title: 'Giám sát từ xa: ',
        desc: 'Theo dõi hoạt động bán hàng mọi lúc, mọi nơi, kịp thời phát hiện và xử lý sự cố ngay khi phát sinh.',
      },
      {
        title: 'Quản lý nhiều cửa hàng:',
        desc: 'Một tài khoản duy nhất,  kiểm soát toàn bộ hệ thống, nâng cao hiệu quả vận hành.',
      },
    ],
    bgClassName: 'bg-primary-50',
    textClassName: 'text-primary-700',
    borderClassName: 'border-primary-700/40',
    bulletClassName: 'bg-primary-700',
  },
  {
    icon: <Zap />,
    image: Feature7Image,
    title: 'Thao tác\nsiêu gọn',
    subtitle: ' Thao tác siêu gọn – Nhanh hơn, mượt hơn, hiệu quả hơn!',
    desc: 'Tối ưu trải nghiệm vận hành với các thao tác nhanh chóng, mượt mà. Từ cập nhật tình trạng sản phẩm đến xử lý thanh toán, tất cả đều được tự động hóa chỉ trong vài chạm, giúp nâng cao hiệu suất làm việc và mang đến sự tiện lợi tối đa.',
    features: [
      {
        title: 'Từ order đến thanh toán chỉ trong vài thao tác:',
        desc: 'Giao diện trực quan, quy trình tối ưu, giúp đơn giản hóa từng bước và tăng tốc xử lý.',
      },
      {
        title: 'Cập nhật tình trạng sản phẩm tức thì:',
        desc: 'Một chạm để hiển thị trạng thái còn hàng hay hết hàng, hỗ trợ quản lý sản phẩm hiệu quả.',
      },
      {
        title: 'Thay đổi trạng thái món ăn siêu nhanh:',
        desc: 'Một chạm duy nhất để cập nhật trạng thái món, đồng bộ giữa bếp và phục vụ, đảm bảo quy trình vận hành trơn tru, giảm thiểu sai sót.',
      },
      {
        title: 'Xuất hóa đơn chỉ trong tích tắc:',
        desc: 'Một lần nhấn là hoàn tất, không còn những thao tác phức tạp.',
      },
      {
        title: 'Thanh toán siêu tốc, chia sẻ QR Code tức thì:',
        desc: 'Dễ dàng tạo và chia sẻ mã QR chỉ với vài bước, giúp giao dịch nhanh chóng, chính xác và tiện lợi.',
      },
    ],
    bgClassName: 'bg-amber-50',
    textClassName: 'text-amber-700',
    borderClassName: 'border-amber-700/40',
    bulletClassName: 'bg-amber-700',
  },
  {
    icon: <Zap />,
    image: Feature6Image,
    title: 'Thanh toán\nsiêu nhanh',
    subtitle:
      'Thanh toán siêu nhanh - Tăng tốc quy trình, nâng cao trải nghiệm',
    desc: 'Zump tối ưu hóa quy trình thanh toán, giúp giao dịch diễn ra nhanh chóng, chính xác và thuận tiện hơn bao giờ hết. Giảm thời gian chờ đợi - Tăng sự hài lòng cho khách hàng!',
    features: [
      {
        title: 'Thanh toán tự động, nhanh chóng:',
        desc: 'Hệ thống tự động tính toán hóa đơn, áp dụng chiết khấu chính xác, giúp hoàn thành giao dịch chỉ trong vài giây, tối ưu hóa thời gian và giảm thiểu sai sót.',
      },
      {
        title: 'Đa kênh thanh toán linh hoạt:',
        desc: 'Từ tiền mặt, chuyển khoản ngân hàng đến ví điện tử (MoMo, ZaloPay, …), mọi phương thức đều có sẵn, mang lại sự tiện lợi tối đa cho khách hàng.',
      },
      {
        title: 'Mã QR riêng cho từng giao dịch:',
        desc: 'Với tính năng tự động tạo mã QR cho từng đơn hàng, hệ thống đảm bảo giao dịch chính xác tuyệt đối, loại bỏ hoàn toàn nguy cơ nhầm lẫn.',
      },
      {
        title: 'Tra soát giao dịch minh bạch:',
        desc: 'Quản lý hình ảnh giao dịch và kiểm tra lịch sử thanh toán một cách nhanh chóng và rõ ràng, giúp nắm bắt mọi thông tin một cách dễ dàng.',
      },
    ],
    bgClassName: 'bg-green-50',
    textClassName: 'text-green-700',
    borderClassName: 'border-green-700/40',
    bulletClassName: 'bg-green-700',
  },
  {
    icon: <QrCode />,
    image: Feature5Image,
    title: 'QR gọi món\ntiện lợi',
    subtitle: 'QR Gọi món - Đặt hàng nhanh chóng, phục vụ chuyên nghiệp',
    desc: 'Tích hợp QR Gọi món giúp khách hàng đặt món dễ dàng, giảm tải áp lực cho nhân viên và rút ngắn thời gian chờ đợi, tránh sai sót. Zump - giải pháp hiện đại giúp tối ưu quy trình phục vụ.',
    features: [
      {
        title: 'Tối ưu quản lý bàn ăn:',
        desc: 'Giảm tải công việc, tránh tình trạng quá tải vào giờ cao điểm.',
      },
      {
        title: 'Đồng bộ đơn hàng:',
        desc: 'Xử lý đơn nhanh chóng, khách không phải chờ đợi lâu.',
      },
      {
        title: 'Theo dõi trạng thái đơn:',
        desc: 'Cả nhân viên và khách hàng đều dễ dàng kiểm soát quy trình phục vụ.',
      },
      {
        title: 'Cá nhân hóa trải nghiệm:',
        desc: 'Khách hàng có thể ghi chú yêu cầu đặc biệt ngay khi đặt món và gọi nhân viên tại bàn chỉ với một chạm.',
      },
    ],
    bgClassName: 'bg-violet-50',
    textClassName: 'text-violet-700',
    borderClassName: 'border-violet-700/40',
    bulletClassName: 'bg-violet-700',
  },
  {
    icon: <ShoppingBag />,
    image: Feature4Image,
    title: 'Trang bán hàng\nmiễn phí',
    subtitle: 'Trang bán hàng miễn phí - Tăng doanh thu, mở rộng khách hàng',
    desc: 'Không còn chờ đợi hay thao tác rườm rà! Hệ thống xuất hóa đơn online giúp mọi giao dịch diễn ra nhanh chóng, chính xác và chuyên nghiệp. Với QR biên lai và hóa đơn ảnh, khách hàng có thể nhận hóa đơn ngay tức thì, tiết kiệm thời gian và nâng cao trải nghiệm mua sắm.',
    features: [
      {
        title: 'Tiết kiệm chi phí:',
        desc: 'Không cần đầu tư website riêng, tối ưu hóa ngân sách cho các chiến lược kinh doanh.',
      },
      {
        title: 'Xử lý đơn hàng nhanh chóng:',
        desc: 'Nhận và quản lý đơn từ xa, giảm thiểu sai sót, nâng cao hiệu suất vận hành.',
      },
      {
        title: 'Cập nhật menu dễ dàng:',
        desc: 'Thêm, sửa, xóa món ăn chỉ với vài thao tác, đảm bảo thông tin luôn chính xác.',
      },
      {
        title: 'Giao diện thân thiện:',
        desc: 'Khách hàng đặt món và thanh toán dễ dàng, mang đến trải nghiệm mua mượt mà, chuyên nghiệp.',
      },
    ],
    bgClassName: 'bg-blue-50',
    textClassName: 'text-blue-700',
    borderClassName: 'border-blue-700/40',
    bulletClassName: 'bg-blue-700',
  },
  {
    icon: <HandPlatter />,
    image: Feature3Image,
    title: 'Xuất hoá đơn\nonline',
    subtitle: 'Xuất hoá đơn online - Nhanh chóng, tiện lợi, chuyên nghiệp',
    desc: 'Zump cung cấp trang bán hàng online hoàn toàn miễn phí, giúp tiếp cận nhiều khách hàng hơn mà không cần đầu tư chi phí xây dựng website. Đơn giản, tiện lợi và tối ưu hóa hiệu suất kinh doanh!',
    features: [
      {
        title: 'QR biên lai – Quét mã, nhận hóa đơn ngay:',
        desc: 'Hệ thống tự động tạo QR biên lai, khách hàng chỉ cần quét mã để nhận hóa đơn nhanh chóng và chính xác.',
      },
      {
        title: 'Hóa đơn ảnh – Chia sẻ nhanh, tiện lợi:',
        desc: 'Gửi trực tiếp hình ảnh hóa đơn đến khách hàng chỉ trong vài giây,  tiết kiệm thời gian và tối ưu trải nghiệm.',
      },
    ],
    bgClassName: 'bg-yellow-50',
    textClassName: 'text-yellow-700',
    borderClassName: 'border-yellow-700/40',
    bulletClassName: 'bg-yellow-700',
  },
  {
    icon: <ChartLine />,
    image: Feature2Image,
    title: 'Báo cáo\nchi tiết',
    subtitle: 'Báo cáo chi tiết - Kiểm soát tài chính, tối ưu lợi nhuận',
    desc: 'Zump mang đến hệ thống báo cáo trực quan, giúp chủ cửa hàng theo dõi tài chính và hiệu suất kinh doanh một cách chính xác. Dữ liệu được trình bày khoa học, hỗ trợ đưa ra quyết định nhanh chóng và nâng cao hiệu suất kinh doanh.',
    features: [
      {
        title: 'Báo cáo doanh thu chi tiết:',
        desc: 'Theo dõi doanh thu theo ngày, tuần, tháng, quý, đánh giá chính xác hiệu suất kinh doanh.',
      },
      {
        title: 'Quản lý dòng tiền hiệu quả:',
        desc: 'Kiểm soát thu chi, phân tích dòng tiền để đảm bảo tài chính luôn ổn định và minh bạch.',
      },
      {
        title: 'Biểu đồ trực quan:',
        desc: 'Dữ liệu được hiển thị dưới dạng biểu đồ sinh động, giúp nắm bắt xu hướng kinh doanh dễ dàng.',
      },
      {
        title: 'Thống kê sản phẩm bán chạy:',
        desc: 'Phân tích số lượng, đơn hàng, doanh thu để xây dựng chiến lược kinh doanh tối ưu.',
      },
    ],
    bgClassName: 'bg-cyan-50',
    textClassName: 'text-cyan-700',
    borderClassName: 'border-cyan-700/40',
    bulletClassName: 'bg-cyan-700',
  },
  {
    icon: <HandPlatter />,
    image: Feature1Image,
    title: 'Tất cả\ntrong một',
    subtitle: 'Tất cả trong một - Giải pháp quản lý toàn diện',
    desc: 'Zump không chỉ đơn thuần hỗ trợ bán hàng mà còn tối ưu toàn bộ quy trình quản lý, từ nhân sự, tài chính đến vận hành chuỗi cửa hàng. Một nền tảng duy nhất, giúp doanh nghiệp kiểm soát chặt chẽ - nâng cao hiệu suất - tối ưu lợi nhuận!',
    features: [
      {
        title: 'Bán hàng nhanh chóng:',
        desc: 'Số hóa quy trình từ order đến thanh toán, đảm bảo giao dịch mượt mà, chính xác, tiết kiệm thời gian.',
      },
      {
        title: 'Quản lý sản phẩm thông minh:',
        desc: 'Cập nhật giá cả, danh mục linh hoạt, hạn chế sai sót, tối ưu chi phí vận hành.',
      },
      {
        title: 'Giám sát nhân sự hiệu quả:',
        desc: 'Hệ thống phân quyền chặt chẽ, theo dõi hiệu suất làm việc của nhân viên theo thời gian thực.',
      },
      {
        title: 'Kiểm soát tài chính minh bạch:',
        desc: 'Tự động ghi nhận thu chi, cung cấp báo cáo tài chính chi tiết, chủ động kiểm soát dòng tiền, ngăn chặn thất thoát.',
      },
      {
        title: 'Phân tích kinh doanh chuyên sâu:',
        desc: 'Báo cáo real-time, trực quan, hỗ trợ ra quyết định chính xác, tối ưu chiến lược phát triển.',
      },
    ],
    bgClassName: 'bg-orange-50',
    textClassName: 'text-orange-700',
    borderClassName: 'border-orange-700/40',
    bulletClassName: 'bg-orange-700',
  },
];

export default function HeroHighlights() {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>();
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 1,
        ease: 'easeOut',
      }}
      className="relative flex flex-col gap-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex gap-1 overflow-x-auto"
      >
        <Swiper
          modules={[Thumbs]}
          freeMode
          slidesPerView="auto"
          watchSlidesProgress
          onSwiper={setThumbsSwiper}
        >
          {highlights.map((highlight, i) => (
            <SwiperSlide key={i} className="!w-40 p-1">
              <div
                key={i}
                className={`button-shinny flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-2 transition-transform duration-500 hover:scale-105 hover:cursor-pointer hover:after:rounded-lg hover:after:bg-gray-200/20 ${highlight.bgClassName} ${highlight.borderClassName} ${highlight.textClassName}`}
              >
                <div>{highlight.icon}</div>
                <p className="whitespace-pre text-center text-sm font-medium text-inherit sm:text-base">
                  {highlight.title}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </motion.div>
      <div className="relative mx-auto w-full overflow-hidden rounded-3xl shadow-xl shadow-primary-200/40">
        <Swiper
          modules={[Autoplay, Thumbs, Navigation]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          watchSlidesProgress
          speed={700}
          spaceBetween={0}
          thumbs={{ swiper: thumbsSwiper, autoScrollOffset: 1 }}
          navigation
          onBeforeInit={(swiper) => {
            swiper.navigation.nextEl = nextRef.current as HTMLElement;
            swiper.navigation.prevEl = prevRef.current as HTMLElement;
          }}
        >
          {highlights.map((highlight, i) => (
            <SwiperSlide
              key={i}
              className={`flex !h-auto flex-col pb-28 ${highlight.bgClassName}`}
            >
              <div className="">
                <div className="m-8">
                  <motion.h2
                    className={`flex items-center gap-2 text-left ${highlight.textClassName}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    {highlight.subtitle}
                  </motion.h2>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p className="mt-4 text-left md:text-lg">
                      {highlight.desc}
                    </p>
                  </motion.div>
                </div>
                <div className={`grid h-full grow grid-cols-1 lg:grid-cols-5`}>
                  <div
                    className={`m-5 rounded-2xl border bg-white p-5 lg:col-span-3 lg:p-8 ${highlight.borderClassName}`}
                  >
                    <ul className="md:mx-8 md:mt-10">
                      {highlight.features.map((feature, j) => (
                        <motion.li
                          key={j}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: '-50px' }}
                          transition={{
                            duration: 0.5,
                            delay: j * 0.1,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="mt-4"
                        >
                          <div
                            className={`mr-2 inline-block size-3 rounded-full ${highlight.bulletClassName}`}
                          />
                          <span className="mr-2 font-medium">
                            {feature.title}
                          </span>
                          <span className="font-light">{feature.desc}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  <motion.div
                    className="relative flex size-full items-center justify-center p-10 lg:col-span-2"
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: 'reverse',
                    }}
                  >
                    <Image
                      src={highlight.image}
                      alt={highlight.title}
                      height={512}
                    />
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute bottom-8 z-10 flex w-full justify-center gap-5">
          <IconButton
            ref={prevRef}
            icon={<ChevronLeft />}
            className="button-shinny rounded-full !p-4"
            aria-label="Previous feature"
          />
          <IconButton
            ref={nextRef}
            icon={<ChevronRight />}
            className="button-shinny rounded-full !p-4"
            aria-label="Next feature"
          />
        </div>
      </div>
    </motion.div>
  );
}
